import { useLazyQuery, useQuery } from '@apollo/client';
import { useCallback, useContext, useMemo } from 'react';
import { ApiTenderFreeResponse } from './types';
import { GET_FREE_TENDER, GET_TENDER_FREE_FILE, GET_TENDER_ID } from './queries';
import { TenderFreeValue, TenderFreeContext } from './context';
import TenderFree from './index';
import { downloadFile, isString, useQueryParams } from 'src/helpers';
import { ApiType } from 'src/lib/API/graphql/types';
import { isFreeUser, useApp } from '../../auth';

export function useTenderFreeCtx(): TenderFreeValue {
  const qp = useQueryParams();
  const token = isString(qp['auth']) ? qp['auth'] : undefined;
  const query = useQuery<ApiTenderFreeResponse>(GET_FREE_TENDER, {
    context: { headers: { [`Tender-Authorization`]: token }, api: ApiType.Public },
    skip: !token
  });

  return useMemo(
    () => ({
      loading: query.loading,
      data: query.data?.getSimpleTender ? new TenderFree(query.data.getSimpleTender) : undefined,
      isExpired: !!query.error && query.error.message.includes('Access denied!')
    }),
    [query]
  );
}
export function useTenderId(): { id?: string; loading: boolean } {
  const { user } = useApp();
  const qp = useQueryParams();
  const token = isString(qp['auth']) ? qp['auth'] : undefined;
  const query = useQuery<{ getRedirectId: { redirectId: string } }>(GET_TENDER_ID, {
    context: { headers: { [`Tender-Authorization`]: token }, api: ApiType.Public },
    skip: !token || isFreeUser(user)
  });

  return useMemo(
    () => ({
      loading: query.loading,
      id: query.data?.getRedirectId.redirectId
    }),
    [query]
  );
}

export function useTenderFree(): TenderFreeValue {
  return useContext(TenderFreeContext);
}

type TenderFreeFileRequest = {
  filePath: string;
};
type TenderFreeFileResponse = {
  getFile: TenderFreeFile;
};
type TenderFreeFile = { url: string };
export function useDownloadFreeFile(): {
  getFile: (filePath: string) => void;
  loading: boolean;
} {
  const qp = useQueryParams();
  const token = isString(qp['auth']) ? qp['auth'] : undefined;
  const [getFileLazy, { loading }] = useLazyQuery<TenderFreeFileResponse, TenderFreeFileRequest>(GET_TENDER_FREE_FILE, {
    context: { headers: { [`Tender-Authorization`]: token }, api: ApiType.Public }
  });

  const getFile = useCallback(
    filePath => {
      return getFileLazy({ variables: { filePath } }).then(res => {
        if (res.error) {
          return;
        }
        const url = res.data?.getFile?.url;
        if (url) {
          downloadFile(url, filePath);
        }
      });
    },
    [getFileLazy]
  );

  return useMemo(() => ({ getFile, loading }), [getFile, loading]);
}
